// Public visit booking page — VisitManager free slots + UI/UX polish.
import { createFileRoute } from '@tanstack/react-router';
import { fetchBackend } from '~/helper/fetchBackend';
import { VisitBookingWidgetGate as VisitBookingWidget } from '~/components/widget/VisitBookingWidgetGate';

interface PrefillContact {
  readonly salutation?: '0' | '1' | null;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly street?: string;
  readonly no?: string;
  readonly zip?: string;
  readonly city?: string;
}

interface BookLoaderData {
  readonly contact?: PrefillContact;
}

/**
 * The expose link path carries `{contactId}`. When it is a REAL numeric HubSpot
 * id we prefill the booking form from that contact (via the immodossier server
 * prefill proxy → unified-api). When it is the literal `public` placeholder (or
 * any non-numeric value) we behave exactly as before: no prefill, no error.
 */
function isRealContactId(contactId: string | undefined): contactId is string {
  return typeof contactId === 'string' && /^\d+$/.test(contactId);
}

export const Route = createFileRoute(
  '/$lang/$companyId/public/expose/$contactId/$propertyId/book' as any,
)({
  loader: async ({ params }): Promise<BookLoaderData> => {
    const { lang, companyId, contactId, propertyId } = params as {
      lang: string;
      companyId: string;
      contactId: string;
      propertyId: string;
    };

    if (!isRealContactId(contactId)) {
      return {};
    }

    try {
      const response = await fetchBackend(
        `/${lang}/${companyId}/public/expose/${contactId}/${propertyId}/prefill/${contactId}`,
      );
      if (!response.ok) {
        return {};
      }
      const contact = (await response.json()) as PrefillContact;
      return { contact };
    } catch {
      // Prefill is a best-effort enhancement — never block the booking page.
      return {};
    }
  },
  component: function ExposeBookPage() {
    const params = Route.useParams();
    const { contact } = Route.useLoaderData();
    return (
      <VisitBookingWidget
        params={params as any}
        contact={contact}
      />
    );
  },
});
